'use client';

import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFetch } from '@gitroom/helpers/utils/custom.fetch';
import { useMediaDirectory } from '@gitroom/react/helpers/use.media.directory';

export type BrandingData = {
  brandName: string;
  logoPath: string | null;
  logoTextPath: string | null;
  faviconPath: string | null;
};

const DEFAULT_BRANDING: BrandingData = {
  brandName: 'Postiz',
  logoPath: null,
  logoTextPath: null,
  faviconPath: null,
};

const BrandingContext = createContext<{
  branding: BrandingData;
  logoUrl: string | null;
  logoTextUrl: string | null;
  faviconUrl: string | null;
  refresh: () => Promise<void>;
  loading: boolean;
}>({
  branding: DEFAULT_BRANDING,
  logoUrl: null,
  logoTextUrl: null,
  faviconUrl: null,
  refresh: async () => undefined,
  loading: true,
});

export const BrandingProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const fetch = useFetch();
  const mediaDirectory = useMediaDirectory();
  const [branding, setBranding] = useState<BrandingData>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await (await fetch('/public/branding')).json();
      setBranding({
        brandName: data.brandName || DEFAULT_BRANDING.brandName,
        logoPath: data.logoPath || null,
        logoTextPath: data.logoTextPath || null,
        faviconPath: data.faviconPath || null,
      });
    } catch {
      setBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [fetch]);

  useEffect(() => {
    void load();
  }, [load]);

  const resolve = useCallback(
    (path: string | null) => (path ? mediaDirectory.set(path) : null),
    [mediaDirectory]
  );

  const value = useMemo(
    () => ({
      branding,
      logoUrl: resolve(branding.logoPath),
      logoTextUrl: resolve(branding.logoTextPath),
      faviconUrl: resolve(branding.faviconPath),
      refresh: load,
      loading,
    }),
    [branding, loading, load, resolve]
  );

  return (
    <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
  );
};

export const useBranding = () => useContext(BrandingContext);
